import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Settings, 
  Save, 
  AlertTriangle, 
  Clock, 
  Calendar,
  Forward,
  TrendingUp,
  CheckCircle,
  XCircle,
  Info,
  Bell,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';
import escalationService from '../../services/escalationService';

const EscalationSettings = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('general');

  // Main config query
  const { data: configData, isLoading } = useQuery({
    queryKey: ['escalation-config'],
    queryFn: () => escalationService.getConfig()
  });

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: escalationService.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries(['escalation-config']);
      toast.success('Escalation settings updated successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  // Calculate forward time mutation
  const calculateForwardTimeMutation = useMutation({
    mutationFn: escalationService.calculateForwardTime,
    onSuccess: (data) => {
      toast.success(`Forward time calculated: ${escalationService.formatMinutes(data.data.calculation.forward_time_minutes)}`);
    },
    onError: (error) => toast.error(error.message)
  });

  const config = configData?.data?.config || {};
  const systemStatus = configData?.data?.system_status || {};
  const categoryStats = configData?.data?.category_stats || [];

  // State for different configuration sections
  const [prioritySettings, setPrioritySettings] = useState({});
  const [autoForwardSettings, setAutoForwardSettings] = useState({
    auto_forward_enabled: true,
    auto_forward_time_minutes: 30,
    auto_forward_same_expertise_only: true,
    auto_forward_max_attempts: 3,
    auto_forward_exclude_weekends: false,
    auto_forward_exclude_holidays: false
  });
  const [businessHoursSettings, setBusinessHoursSettings] = useState({
    business_hours_enabled: false,
    business_hours: {
      start_time: '09:00',
      end_time: '17:00',
      timezone: 'UTC',
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    }
  });
  const [notificationSettings, setNotificationSettings] = useState({
    notify_customer: true,
    notify_agent: true,
    notify_customer_care: true,
    notify_admin: true,
    auto_forward_notifications: true,
    escalation_notifications: true
  });

  // Update state when config loads
  useEffect(() => {
    if (config) {
      setPrioritySettings(config.priority_settings || {});
      setAutoForwardSettings({
        auto_forward_enabled: config.auto_forward_enabled ?? true,
        auto_forward_time_minutes: config.auto_forward_time_minutes || 30,
        auto_forward_same_expertise_only: config.auto_forward_same_expertise_only ?? true,
        auto_forward_max_attempts: config.auto_forward_max_attempts || 3,
        auto_forward_exclude_weekends: config.auto_forward_exclude_weekends || false,
        auto_forward_exclude_holidays: config.auto_forward_exclude_holidays || false
      });
      setBusinessHoursSettings({
        business_hours_enabled: config.business_hours_enabled || false,
        business_hours: config.business_hours || {
          start_time: '09:00',
          end_time: '17:00',
          timezone: 'UTC',
          days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
        }
      });
      setNotificationSettings(config.notification_settings || {
        notify_customer: true,
        notify_agent: true,
        notify_customer_care: true,
        notify_admin: true,
        auto_forward_notifications: true,
        escalation_notifications: true
      });
    }
  }, [config]);

  const handleSaveConfig = () => {
    const configData = {
      // Priority settings
      priority_settings: prioritySettings,
      default_priority: config.default_priority || 'medium',
      
      // Auto-forward settings
      ...autoForwardSettings,
      
      // Auto-escalation
      auto_escalation_enabled: config.auto_escalation_enabled ?? true,
      
      // Business hours
      ...businessHoursSettings,
      
      // Holidays
      holidays: config.holidays || [],
      
      // Escalation hierarchy
      escalation_hierarchy: config.escalation_hierarchy || [],
      
      // Notifications
      notification_settings: notificationSettings
    };

    updateConfigMutation.mutate(configData);
  };

  const handleTestForwardTime = (priority, categoryId = null) => {
    calculateForwardTimeMutation.mutate({ priority, category_id: categoryId });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'priority', label: 'Priority SLA', icon: TrendingUp },
    { id: 'forwarding', label: 'Auto-Forward', icon: Forward },
    { id: 'business-hours', label: 'Business Hours', icon: Calendar },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Settings className="w-6 h-6 mr-2" />
          Escalation Settings
        </h1>
        <p className="mt-2 text-gray-600">
          Configure comprehensive escalation and auto-forwarding rules
        </p>
      </div>

      {/* System Status Overview */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">System Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center">
            {systemStatus.sla_configured ? 
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" /> :
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
            }
            <span className="text-sm">SLA Configured</span>
          </div>
          <div className="flex items-center">
            {systemStatus.auto_forward_enabled ? 
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" /> :
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
            }
            <span className="text-sm">Auto-Forward Active</span>
          </div>
          <div className="flex items-center">
            {systemStatus.auto_escalation_enabled ? 
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" /> :
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
            }
            <span className="text-sm">Auto-Escalation Active</span>
          </div>
          <div className="flex items-center">
            {systemStatus.categories_with_agents > 0 ? 
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" /> :
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
            }
            <span className="text-sm">{systemStatus.categories_with_agents} Categories Ready</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">General Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Priority Level
                  </label>
                  <select
                    value={config.default_priority || 'medium'}
                    onChange={(e) => {
                      const configData = {
                        ...config,
                        default_priority: e.target.value
                      };
                      updateConfigMutation.mutate(configData);
                    }}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    {escalationService.getPriorityOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Default priority assigned to new tickets
                  </p>
                </div>

                <div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="auto_escalation"
                      checked={config.auto_escalation_enabled ?? true}
                      onChange={(e) => {
                        const configData = {
                          ...config,
                          auto_escalation_enabled: e.target.checked
                        };
                        updateConfigMutation.mutate(configData);
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="auto_escalation" className="ml-2 block text-sm text-gray-900">
                      Enable Auto-Escalation
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Automatically escalate tickets based on SLA times
                  </p>
                </div>
              </div>

              {/* Category Statistics */}
              <div className="mt-8">
                <h3 className="text-md font-medium text-gray-900 mb-4">Category Overview</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {categoryStats.slice(0, 6).map(cat => (
                      <div key={cat.id} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div>
                          <span className="text-sm font-medium text-gray-900">{cat.name}</span>
                          <p className="text-xs text-gray-500">{cat.agents_with_expertise} agents</p>
                        </div>
                        {cat.can_auto_forward ? 
                          <CheckCircle className="w-4 h-4 text-green-500" /> :
                          <XCircle className="w-4 h-4 text-red-500" />
                        }
                      </div>
                    ))}
                  </div>
                  {categoryStats.length > 6 && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      And {categoryStats.length - 6} more categories...
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Priority SLA Settings Tab */}
          {activeTab === 'priority' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Priority-based SLA Times</h2>
              
              <div className="space-y-4">
                {escalationService.getPriorityOptions().map(priority => (
                  <div key={priority.value} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`font-medium capitalize text-${priority.color}-600`}>
                        {priority.label} Priority
                      </h3>
                      <button
                        onClick={() => handleTestForwardTime(priority.value)}
                        disabled={calculateForwardTimeMutation.isLoading}
                        className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
                      >
                        Test Forward Time
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Response Time (hours)
                        </label>
                        <input
                          type="number"
                          min="0.25"
                          max="168"
                          step="0.25"
                          value={prioritySettings[priority.value]?.response_time || ''}
                          onChange={(e) => setPrioritySettings({
                            ...prioritySettings,
                            [priority.value]: {
                              ...prioritySettings[priority.value],
                              response_time: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="e.g., 2"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          15 minutes to 1 week (0.25-168 hours)
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Escalation Time (hours)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="336"
                          value={prioritySettings[priority.value]?.escalation_time || ''}
                          onChange={(e) => setPrioritySettings({
                            ...prioritySettings,
                            [priority.value]: {
                              ...prioritySettings[priority.value],
                              escalation_time: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="e.g., 8"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          1 hour to 2 weeks (1-336 hours)
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Auto-Forward Settings Tab */}
          {activeTab === 'forwarding' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Auto-Forward Settings</h2>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="auto_forward_enabled"
                    checked={autoForwardSettings.auto_forward_enabled}
                    onChange={(e) => setAutoForwardSettings({
                      ...autoForwardSettings,
                      auto_forward_enabled: e.target.checked
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="auto_forward_enabled" className="ml-2 block text-sm text-gray-900">
                    Enable Auto-Forward System
                  </label>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex">
                    <Info className="h-5 w-5 text-blue-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">Dynamic Forward Time</h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>
                          Forward time is now configurable and applies uniformly to ALL categories. 
                          No per-category settings needed.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Auto-Forward Time (minutes)
                    </label>
                    <select
                      value={autoForwardSettings.auto_forward_time_minutes}
                      onChange={(e) => setAutoForwardSettings({
                        ...autoForwardSettings,
                        auto_forward_time_minutes: parseInt(e.target.value)
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      {escalationService.getTimeSuggestions().map(suggestion => (
                        <option key={suggestion.value} value={suggestion.value}>
                          {suggestion.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Time before forwarding unresponded tickets
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Maximum Forward Attempts
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={autoForwardSettings.auto_forward_max_attempts}
                      onChange={(e) => setAutoForwardSettings({
                        ...autoForwardSettings,
                        auto_forward_max_attempts: parseInt(e.target.value) || 1
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Maximum number of forward attempts per ticket
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="same_expertise_only"
                      checked={autoForwardSettings.auto_forward_same_expertise_only}
                      onChange={(e) => setAutoForwardSettings({
                        ...autoForwardSettings,
                        auto_forward_same_expertise_only: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="same_expertise_only" className="ml-2 block text-sm text-gray-900">
                      Only forward to agents with matching category expertise
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="exclude_weekends"
                      checked={autoForwardSettings.auto_forward_exclude_weekends}
                      onChange={(e) => setAutoForwardSettings({
                        ...autoForwardSettings,
                        auto_forward_exclude_weekends: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="exclude_weekends" className="ml-2 block text-sm text-gray-900">
                      Exclude weekends from auto-forwarding
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="exclude_holidays"
                      checked={autoForwardSettings.auto_forward_exclude_holidays}
                      onChange={(e) => setAutoForwardSettings({
                        ...autoForwardSettings,
                        auto_forward_exclude_holidays: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="exclude_holidays" className="ml-2 block text-sm text-gray-900">
                      Exclude holidays from auto-forwarding
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Business Hours Settings Tab */}
          {activeTab === 'business-hours' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Business Hours Configuration</h2>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="business_hours_enabled"
                    checked={businessHoursSettings.business_hours_enabled}
                    onChange={(e) => setBusinessHoursSettings({
                      ...businessHoursSettings,
                      business_hours_enabled: e.target.checked
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="business_hours_enabled" className="ml-2 block text-sm text-gray-900">
                    Enable Business Hours Restrictions
                  </label>
                </div>

                {businessHoursSettings.business_hours_enabled && (
                  <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={businessHoursSettings.business_hours.start_time}
                          onChange={(e) => setBusinessHoursSettings({
                            ...businessHoursSettings,
                            business_hours: {
                              ...businessHoursSettings.business_hours,
                              start_time: e.target.value
                            }
                          })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          End Time
                        </label>
                        <input
                          type="time"
                          value={businessHoursSettings.business_hours.end_time}
                          onChange={(e) => setBusinessHoursSettings({
                            ...businessHoursSettings,
                            business_hours: {
                              ...businessHoursSettings.business_hours,
                              end_time: e.target.value
                            }
                          })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Timezone
                        </label>
                        <select
                          value={businessHoursSettings.business_hours.timezone}
                          onChange={(e) => setBusinessHoursSettings({
                            ...businessHoursSettings,
                            business_hours: {
                              ...businessHoursSettings.business_hours,
                              timezone: e.target.value
                            }
                          })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time</option>
                          <option value="America/Chicago">Central Time</option>
                          <option value="America/Denver">Mountain Time</option>
                          <option value="America/Los_Angeles">Pacific Time</option>
                          <option value="Europe/London">GMT</option>
                          <option value="Europe/Berlin">Central European Time</option>
                          <option value="Asia/Tokyo">Japan Standard Time</option>
                          <option value="Asia/Shanghai">China Standard Time</option>
                          <option value="Asia/Kolkata">India Standard Time</option>
                          <option value="Asia/Dubai">Gulf Standard Time</option>
                          <option value="Asia/Dhaka">Bangladesh Standard Time</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Days
                      </label>
                      <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                        {escalationService.getBusinessDayOptions().map(day => (
                          <label key={day.value} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={businessHoursSettings.business_hours.days.includes(day.value)}
                              onChange={(e) => {
                                const days = e.target.checked 
                                  ? [...businessHoursSettings.business_hours.days, day.value]
                                  : businessHoursSettings.business_hours.days.filter(d => d !== day.value);
                                
                                setBusinessHoursSettings({
                                  ...businessHoursSettings,
                                  business_hours: {
                                    ...businessHoursSettings.business_hours,
                                    days
                                  }
                                });
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-1 text-xs text-gray-700">{day.label.slice(0, 3)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notifications Settings Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Notification Settings</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">General Notifications</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="notify_customer"
                        checked={notificationSettings.notify_customer}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          notify_customer: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="notify_customer" className="ml-2 block text-sm text-gray-900">
                        Notify customers about escalations
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="notify_agent"
                        checked={notificationSettings.notify_agent}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          notify_agent: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="notify_agent" className="ml-2 block text-sm text-gray-900">
                        Notify agents about ticket assignments
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="notify_customer_care"
                        checked={notificationSettings.notify_customer_care}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          notify_customer_care: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="notify_customer_care" className="ml-2 block text-sm text-gray-900">
                        Notify customer care team
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="notify_admin"
                        checked={notificationSettings.notify_admin}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          notify_admin: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="notify_admin" className="ml-2 block text-sm text-gray-900">
                        Notify administrators
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">Automated Action Notifications</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="auto_forward_notifications"
                        checked={notificationSettings.auto_forward_notifications}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          auto_forward_notifications: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="auto_forward_notifications" className="ml-2 block text-sm text-gray-900">
                        Send notifications for auto-forwards
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="escalation_notifications"
                        checked={notificationSettings.escalation_notifications}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          escalation_notifications: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="escalation_notifications" className="ml-2 block text-sm text-gray-900">
                        Send notifications for escalations
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {updateConfigMutation.isLoading ? 'Saving changes...' : 'Click save to apply all changes'}
          </div>
          <button
            onClick={handleSaveConfig}
            disabled={updateConfigMutation.isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {updateConfigMutation.isLoading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {/* Current Settings Summary */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Current Configuration Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="font-medium text-blue-900">Auto-Forward Time</h3>
            </div>
            <p className="text-2xl font-bold text-blue-600 mt-2">
              {escalationService.formatMinutes(autoForwardSettings.auto_forward_time_minutes)}
            </p>
            <p className="text-sm text-blue-700">Applies to all categories</p>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-green-600 mr-2" />
              <h3 className="font-medium text-green-900">Ready Categories</h3>
            </div>
            <p className="text-2xl font-bold text-green-600 mt-2">
              {categoryStats.filter(cat => cat.can_auto_forward).length}
            </p>
            <p className="text-sm text-green-700">Out of {categoryStats.length} total</p>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center">
              <TrendingUp className="w-5 h-5 text-yellow-600 mr-2" />
              <h3 className="font-medium text-yellow-900">SLA Priorities</h3>
            </div>
            <p className="text-2xl font-bold text-yellow-600 mt-2">
              {Object.keys(prioritySettings).length}
            </p>
            <p className="text-sm text-yellow-700">Configured levels</p>
          </div>
        </div>
      </div>

      {/* Warning Messages */}
      {(!systemStatus.overall_status || systemStatus.categories_with_agents === 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Configuration Incomplete</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc pl-5 space-y-1">
                  {!systemStatus.sla_configured && (
                    <li>Priority SLA times need to be configured</li>
                  )}
                  {systemStatus.categories_with_agents === 0 && (
                    <li>No categories have agents with expertise assigned</li>
                  )}
                  {!systemStatus.auto_forward_enabled && !systemStatus.auto_escalation_enabled && (
                    <li>Both auto-forward and auto-escalation are disabled</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EscalationSettings;